import DosBundle from "./dos/bundle/dos-bundle";
import emulatorsImpl from "./impl/emulators-impl";

import { AsyncifyStats, TransportLayer, FsNode } from "./protocol/protocol";

export interface DosConfig {
    dosboxConf: string,
    jsdosConf: {
        version: string,
    },
};

/* eslint-disable no-unused-vars */
export enum NetworkType {
    NETWORK_DOSBOX_IPX = 0,
}
/* eslint-enable no-unused-vars */

/**
 * Minimal webrtcnet handle shape consumed by the IPX bridge in
 * protocol.ts. Structurally compatible with `Net` from the parent
 * js-dos package (`src/webrtcnet/webrtcnet.ts`); we re-declare it
 * here to avoid a circular dependency and to keep the emulators
 * package self-contained.
 */
export interface NetBridge {
    peerId: number;
    connected: Set<number>;
    /**
     * Drive the webrtcnet wasm event loop. The wrapper only ticks this
     * during bootstrap (until peerId becomes non-zero) and never again,
     * so the protocol.ts recv pump has to keep calling it — otherwise
     * libwebsockets/libwebrtc service routines starve and existing
     * data channels go quiet under sustained traffic.
     */
    wait: (ms: number) => void;
    sendBinary: (data: Uint8Array, peerId: number) => number;
    recvBinary: () => { data: Uint8Array; peerId: number } | null;
    /**
     * Optional event-driven receive hook — see `NetInstance.onData` in
     * `src/public/types.ts`. Set by the recv pump (protocol.ts
     * `ensureWebrtcPoll`) so a datachannel message can trigger an immediate
     * bounded drain instead of waiting up to WEBRTC_POLL_MS for the next poll
     * tick. Optional so non-webrtc transports (raw WebSocket) need not set it.
     */
    onData?: (() => void) | null;
}

export interface BackendOptions {
    token?: string | undefined;
    onExtractProgress?: (bundleIndex: number, file: string, extracted: number, total: number) => void;
    canvas?: OffscreenCanvas;
    /**
     * When set, ws-net-connect routes numeric addresses (treated as
     * peer ids) and the literal "self" sentinel through webrtcnet's
     * P2P transport instead of opening a raw WebSocket. Without this
     * the legacy bridge ends up calling new WebSocket("ws://<peerId>")
     * which DNS can't resolve. See protocol.ts case "ws-net-connect".
     */
    net?: NetBridge;
    /**
     * Hard disks the host built itself, served to the conf's
     * `imgmount <n> sockdrive <url>` lines by url instead of over the
     * network. The player builds one for every bundle folder a conf mounts
     * next to a booted OS (`mount d .` + `boot c:`): DOSBox-X hands the guest
     * such a folder as a FAT image that lives in RAM only, so whatever a game
     * saved there was gone at the next load. As a sockdrive, its writes land
     * in the same overlay every other sockdrive save already carries.
     */
    localDrives?: LocalDriveImage[];
}

/**
 * A hard disk image held by the page (BackendOptions.localDrives). Ranges in
 * `emptyRanges` are all zeros and never read. The player declares the same
 * shape (src/folder-drives/fat16.ts) — keep the two in step.
 */
export interface LocalDriveImage {
    url: string;
    cylinders: number;
    heads: number;
    sectors: number;
    sectorSize: number;
    sizeBytes: number;
    rangeSize: number;
    rangeCount: number;
    emptyRanges: number[];
    /** A fresh copy of one range, exactly `rangeSize` bytes. */
    readRange(range: number): Uint8Array;
    /** Drop the image bytes once every range is in the emulator. */
    release?(): void;
}

export type InitBundleEntry = Uint8Array;
export interface InitFileEntry {
    path: string,
    contents: Uint8Array,
};
export type InitFsEntry = InitBundleEntry | InitFileEntry | DosConfig | string;
export type InitFs = InitFsEntry | InitFsEntry[];

export type PersistedSockdrives = {
    drives: {
        url: string,
        persist: Uint8Array,
    }[],
} | null;

export interface Emulators {
    // * pathPrefix - by default emulators will load wasm modules relatively from current path,
    // you should specify path prefix if you want to load them from different place
    pathPrefix: string;

    // * pathSuffix - suffix fill be added to end of the each path
    pathSuffix: string;

    // * version - version of emulators build
    version: string;

    // * wdosboxJs - a file name to load
    wdosboxJs: string;

    // * dosBundle - create empty DosBundle
    bundle: () => Promise<DosBundle>;

    // * dosConfig - read bundle config
    bundleConfig: (bundle: InitBundleEntry) => Promise<DosConfig | null>;

    // * updateDosConfig - update bunle config
    bundleUpdateConfig: (bundle: InitBundleEntry, config: DosConfig) => Promise<Uint8Array>;

    // * dosboxNode - create dosbox node emulator backend
    dosboxNode: (init: InitFs, options?: BackendOptions) => Promise<CommandInterface>;

    // * dosboxDirect - create dosbox direct emulator backend
    dosboxDirect: (init: InitFs, options?: BackendOptions) => Promise<CommandInterface>;

    // * dosboxWorker - create dosbox worker emulator backend
    dosboxWorker: (init: InitFs, options?: BackendOptions) => Promise<CommandInterface>;

    // * dosboxJspiDirect - create dosbox direct emulator backend on the JSPI wasm
    dosboxJspiDirect: (init: InitFs, options?: BackendOptions) => Promise<CommandInterface>;

    // * dosboxJspiWorker - create dosbox worker emulator backend on the JSPI wasm
    dosboxJspiWorker: (init: InitFs, options?: BackendOptions) => Promise<CommandInterface>;

    // * dosboxXNode - create dosbox-x node emulator backend
    dosboxXNode: (init: InitFs, options?: BackendOptions) => Promise<CommandInterface>;

    // * dosboxDirect - create dosbox-x direct emulator backend
    dosboxXDirect: (init: InitFs, options?: BackendOptions) => Promise<CommandInterface>;

    // * dosboxWorker - create dosbox-x worker emulator backend
    dosboxXWorker: (init: InitFs, options?: BackendOptions) => Promise<CommandInterface>;

    // * dosboxXJspiDirect - create dosbox-x direct emulator backend on the JSPI wasm
    dosboxXJspiDirect: (init: InitFs, options?: BackendOptions) => Promise<CommandInterface>;

    // * dosboxXJspiWorker - create dosbox-x worker emulator backend on the JSPI wasm
    dosboxXJspiWorker: (init: InitFs, options?: BackendOptions) => Promise<CommandInterface>;

    // * backend - create abstract emulation backend by given TransportLayer
    backend: (init: InitFs, transportLayer: TransportLayer,
        options?: BackendOptions) => Promise<CommandInterface>;
}

export interface CommandInterface {
    // * get bundle config
    config: () => Promise<DosConfig>;

    // * current render buffer width
    height: () => number;

    // * current render buffer height
    width: () => number;

    // * sound frequency
    soundFrequency: () => number;

    // * `screenshot()` - get screnshot of canvas as ImageData
    screenshot: () => Promise<ImageData>;

    // * `pause()` - pause emulation (also mute all sounds)
    pause: () => void;

    // * `resume()` - resume emulation (also unmute all sounds)
    resume: () => void;

    // * `mute()` - mute all sounds
    mute: () => void;

    // * `unmute()` - unmute all sounds
    unmute: () => void;

    // * `exit()` - exit from runtime
    exit: () => Promise<void>;

    // * `simulateKeyPress(...keyCodes)` - allows to simulate key press **AND** release event for key code
    // see `sendKeyPress` to find meaning of keyCode. Key combination is supported when more than 1 keyCode is set.
    simulateKeyPress: (...keyCodes: number[]) => void;

    // * `sendKeyEvent(keyCode, pressed)` - sends single key (press or release) event to backend
    sendKeyEvent: (keyCode: number, pressed: boolean) => void;

    // * `sendMouseMotion` - sends mouse motion event to backend, position is in range [0, 1]
    sendMouseMotion: (x: number, y: number) => void;

    // * `sendRelativeMotion` - sends mouse motion event to backend, position is absolute diff of position
    sendMouseRelativeMotion: (x: number, y: number) => void;

    // * `simulateMouseButton` - sends mouse button event (press or release) to backend
    sendMouseButton: (button: number, pressed: boolean) => void;

    // * `sendMouseSync` - sends mouse sync event
    sendMouseSync: () => void;

    // * `sendBackendEvent` - send event for current backend, event will be stringified to json
    sendBackendEvent: (event: any) => void;

    // dump FS as Uint8Array <zip archive>
    persist(onlyChanges?: boolean): Promise<Uint8Array | PersistedSockdrives | null>;

    // events
    events(): CommandInterfaceEvents;

    networkConnect(networkType: NetworkType, address: string): Promise<void>;

    networkDisconnect(networkType: NetworkType): Promise<void>;

    asyncifyStats(): Promise<AsyncifyStats>;

    fsTree(): Promise<FsNode>;

    fsReadFile(file: string): Promise<Uint8Array>;

    fsWriteFile(file: string, contents: ReadableStream<Uint8Array> | Uint8Array): Promise<void>;

    fsDeleteFile(file: string): Promise<boolean>;

}

export type MessageType = "log" | "warn" | "error" | string;

/** A run of scanlines that changed in a frame: rows `start` to `start + count - 1`. */
export interface FrameBand {
    start: number;
    count: number;
}

export interface CommandInterfaceEvents {
    onStdout: (consumer: (message: string) => void) => void;
    onFrameSize: (consumer: (width: number, height: number) => void) => void;
    /**
     * A new frame. `bands` names the scanlines that changed since the
     * previous one, when the backend knows them (the worker's dirty-line
     * batches); a renderer may upload only those rows. Absent, or when
     * in doubt, the whole frame is new.
     */
    onFrame: (consumer: (rgb: Uint8Array | null, rgba: Uint8Array | null, bands?: FrameBand[]) => void) => void;
    onSoundPush: (consumer: (samples: Float32Array) => void) => void;
    onExit: (consumer: () => void) => void;

    onMessage: (consumer: (msgType: MessageType, ...args: any[]) => void) => void;

    onNetworkConnected: (consumer: (networkType: NetworkType, address: string) => void) => void;
    onNetworkDisconnected: (consumer: (networkType: NetworkType) => void) => void;

    onUnload: (consumer: () => Promise<void>) => void;

    // 3dfx voodoo OpenGL backend lifecycle.  Fires when the in-worker
    // voodoo_ogl_init succeeds (Active, with width/height), when the game
    // changes voodoo resolution mid-session (Resize), and when the voodoo
    // mode is torn down (Inactive).  See docs/3dfx/40-runtime-wiring.md §6.
    onVoodooActive: (consumer: (width: number, height: number) => void) => void;
    onVoodooInactive: (consumer: () => void) => void;
    onVoodooResize: (consumer: (width: number, height: number) => void) => void;

    // Phase 7-r6 — voodoo swap-rate sampler, 1 Hz.  Drives the FPS overlay
    // component in src/window/dos/fps-overlay.tsx.
    onPerformanceFps: (consumer: (fps: number) => void) => void;
}

if (typeof window !== "undefined") {
    (window as any).emulators = emulatorsImpl;
} if (typeof global !== "undefined") {
    (global as any).emulators = emulatorsImpl;
}
